/**
 * A comma BETWEEN TWO DIGITS is a separator, and LaTeX treats every comma as punctuation.
 *
 * ── WHAT THIS FIXES, MEASURED ─────────────────────────────────────────────────────────────────
 * Against katex 0.16.45, the comma in `1,5` is an `mpunct` atom carrying `margin-right: 0.1667em`
 * -- the space that belongs after the comma of a LIST. So `1,5` draws as "1, 5" and `2,403,403`
 * draws as "2, 403, 403". `1{,}5` measures clean: no `mspace`, no margin.
 *
 * ── WHY THIS IS NOT A LOCALE QUESTION ─────────────────────────────────────────────────────────
 * It looked like one. Most of the world writes a decimal comma, so the first reading was that
 * `1,5` means 1.5 in some notations and a coordinate pair in others, and that the editor would
 * have to know which to space it correctly.
 *
 * It does not, because the current behaviour is wrong in BOTH. `2,403,403` is an English
 * thousands separator and it renders with gaps just the same. There is no notation in which a
 * comma between two digits wants the space of a list, so there is nothing to decide and nothing
 * for the notation mode to carry.
 *
 * ── WHAT IT COSTS ─────────────────────────────────────────────────────────────────────────────
 * A coordinate pair typed TIGHT -- `(1,5)` for the point -- loses the space it would have got.
 * That is the whole price, it is paid only by someone who did not type the space they wanted,
 * and `(1, 5)` still spaces normally because a comma followed by a space is not touched.
 *
 * Applied at render time, like the percent escape beside it: content stored before this existed
 * is fixed too, and the stored source stays what the teacher actually typed.
 */

/**
 * A digit, a comma, and a digit after it.
 *
 * The trailing digit is a LOOKAHEAD so that runs are caught: consuming it would step past the
 * second comma of `2,403,403` and leave half the number spaced.
 */
const DIGIT_COMMA_DIGIT = /(\d),(?=\d)/g;

/** `{,}` is an ordinary symbol where a bare `,` is punctuation. Already-tight source is left. */
export function tightenDigitCommas(src: string): string {
  if (!src.includes(',')) return src;
  return src.replace(DIGIT_COMMA_DIGIT, '$1{,}');
}
